import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/auth/Login.tsx';
import Dashboard from './components/dashboard/Dashboard.tsx';
import AlumniDashboard from './components/dashboard/AlumniDashboard.tsx';
import AdminDashboard from './components/admin/AdminDashboard.tsx';
import PasswordChangeForm from './components/auth/PasswordChangeForm.tsx';
import DonatePage from './components/public/DonatePage.tsx';
import PublicInvoicePayment from './components/public/PublicInvoicePayment.tsx';
import ImpersonationBanner from './components/common/ImpersonationBanner.tsx';
import CogginsDocumentViewerHost from './components/shared/CogginsDocumentViewerHost.tsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, mustChangePassword, onPasswordChanged } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-ggaa-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for token in localStorage instead of just user state
  const token = localStorage.getItem('authToken');
  if (!user && !token) {
    return <Navigate to="/login" replace />;
  }

  // If we have a token but no user, allow access (user state might be temporarily cleared)
  if (!user && token) {
    console.log('User state missing but token exists, allowing access');
  }

  // Show password change form if required (only if we have user data)
  if (user && mustChangePassword) {
    return (
      <PasswordChangeForm 
        onSuccess={onPasswordChanged}
        userEmail={user.email}
      />
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen ranch-bg">
          <ImpersonationBanner />
          <CogginsDocumentViewerHost />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/pay-invoice/:token" element={<PublicInvoicePayment />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/alumni" 
              element={
                <ProtectedRoute>
                  <AlumniDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App; 